/**
 * Oath Authentication Context
 * Provides authentication state and methods to the new mobile app routes
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  oathLogin,
  oathLoginWithImpersonationToken,
  oathLogout,
  oathRefreshToken,
  oathIsAuthenticated,
  oathGetStoredUser,
  oathGetCurrentUser,
  oathGetRemainingSessionTime,
} from './oathAuthService';
import { SessionConfig } from '../models/userModels';

const OathAuthContext = createContext(null);

/**
 * Hook to access the Oath auth context
 * @returns {OathAuthContextValue}
 */
export const useOathAuth = () => {
  const context = useContext(OathAuthContext);
  if (!context) {
    throw new Error('useOathAuth must be used within an OathAuthProvider');
  }
  return context;
};

/**
 * @typedef {Object} OathAuthContextValue
 * @property {OathUserInfo | null} user - Current user info
 * @property {boolean} isAuthenticated - Whether user is authenticated
 * @property {boolean} isLoading - Whether auth state is being determined
 * @property {number} sessionTimeRemaining - Milliseconds until session expires
 * @property {boolean} isImpersonation - Whether current session is an impersonation session
 * @property {string} impersonatedBy - Email of the b360 user who initiated impersonation
 * @property {(loginId: string, password: string) => Promise<{success: boolean, error?: string}>} login
 * @property {() => void} logout
 * @property {() => Promise<{success: boolean}>} refreshSession
 */

export const OathAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(0);
  const [warningsShown, setWarningsShown] = useState(new Set());
  const [isImpersonation, setIsImpersonation] = useState(false);
  const [impersonatedBy, setImpersonatedBy] = useState('');

  // Initialize auth state from storage or impersonation token
  useEffect(() => {
    const initAuth = async () => {
      // Check for impersonation token in URL
      const params = new URLSearchParams(window.location.search);
      const impersonationToken = params.get('impersonation_token');

      if (impersonationToken) {
        const result = await oathLoginWithImpersonationToken(impersonationToken);
        if (result.success && result.user) {
          setUser(result.user);
          setIsImpersonation(true);
          setImpersonatedBy(result.impersonatedBy || '');
        }
        // Clean up URL regardless of success/failure
        const url = new URL(window.location);
        url.searchParams.delete('impersonation_token');
        window.history.replaceState({}, '', url);
        setIsLoading(false);
        return;
      }

      // Normal auth: restore from localStorage
      if (oathIsAuthenticated()) {
        const storedUser = oathGetStoredUser();
        if (storedUser) {
          setUser(storedUser);

          // Restore impersonation state from localStorage
          if (localStorage.getItem('isImpersonation') === 'true') {
            setIsImpersonation(true);
            setImpersonatedBy(localStorage.getItem('impersonatedBy') || '');
          } else {
            // Skip /me call right after a fresh login — the login response
            // already has correct permissions and /me may return stale cached data
            const freshLogin = sessionStorage.getItem('oathFreshLogin');
            const isFreshLogin = freshLogin && (Date.now() - parseInt(freshLogin, 10)) < 10000;
            if (isFreshLogin) {
              sessionStorage.removeItem('oathFreshLogin');
            } else {
              // Only refresh user from server for existing sessions (e.g. page reload)
              const result = await oathGetCurrentUser();
              if (result.success && result.user) {
                setUser(result.user);
              }
            }
          }
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Silent user refresh — keeps permissions in sync without page reload
  useEffect(() => {
    if (!user || isImpersonation) return;

    const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

    const interval = setInterval(async () => {
      const result = await oathGetCurrentUser();
      if (result.success && result.user) {
        setUser(result.user);
      }
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [user, isImpersonation]);

  // Session countdown timer
  useEffect(() => {
    if (!user) {
      setSessionTimeRemaining(0);
      return;
    }

    const updateRemainingTime = () => {
      const remaining = oathGetRemainingSessionTime();
      setSessionTimeRemaining(remaining);

      // Auto-logout when session expires
      if (remaining <= 0) {
        const wasImpersonation = localStorage.getItem('isImpersonation') === 'true';
        // Flag session expiry so login page skips return-to-previous-page redirect
        sessionStorage.setItem('oathSessionExpired', 'true');
        oathLogout();
        setUser(null);
        setSessionTimeRemaining(0);
        setWarningsShown(new Set());
        if (wasImpersonation) {
          window.location.href = '/session-expired';
        }
      }
    };

    // Update immediately
    updateRemainingTime();

    // Update every second
    const interval = setInterval(updateRemainingTime, 1000);

    return () => clearInterval(interval);
  }, [user]);

  // Session warning notifications (skip for impersonation — banner shows time instead)
  useEffect(() => {
    if (!user || sessionTimeRemaining <= 0 || isImpersonation) return;

    const minutesRemaining = Math.floor(sessionTimeRemaining / 60000);

    SessionConfig.WARNING_THRESHOLD_MINUTES.forEach((threshold) => {
      if (minutesRemaining <= threshold && !warningsShown.has(threshold)) {
        // Mark warning as shown
        setWarningsShown((prev) => new Set([...prev, threshold]));

        // Dispatch custom event for toast notification
        // Components can listen to this event to show toasts
        window.dispatchEvent(
          new CustomEvent('oath-session-warning', {
            detail: { minutesRemaining: threshold },
          })
        );
      }
    });
  }, [sessionTimeRemaining, user, warningsShown]);

  const handleLogin = useCallback(async (loginId, password) => {
    setIsLoading(true);
    const result = await oathLogin(loginId, password);

    if (result.success && result.user) {
      setUser(result.user);
      setWarningsShown(new Set()); // Reset warnings for new session
    }

    setIsLoading(false);
    return result;
  }, []);

  const handleLogout = useCallback(() => {
    oathLogout();
    setUser(null);
    setSessionTimeRemaining(0);
    setWarningsShown(new Set());
    setIsImpersonation(false);
    setImpersonatedBy('');
    sessionStorage.removeItem('oath_announcements_auto_opened');
  }, []);

  const handleRefreshSession = useCallback(async () => {
    // Impersonation sessions cannot be refreshed
    if (isImpersonation) {
      return { success: false, error: 'Impersonation sessions cannot be extended' };
    }

    const result = await oathRefreshToken();

    if (result.success) {
      setWarningsShown(new Set()); // Reset warnings after refresh
      // Update remaining time immediately
      setSessionTimeRemaining(oathGetRemainingSessionTime());
      // Update user state with fresh permissions from refresh response
      if (result.user) {
        setUser(result.user);
      }
    } else {
      // Refresh failed, logout
      handleLogout();
    }

    return result;
  }, [handleLogout, isImpersonation]);

  const value = {
    user,
    isAuthenticated: !!user && oathIsAuthenticated(),
    isLoading,
    sessionTimeRemaining,
    isImpersonation,
    impersonatedBy,
    login: handleLogin,
    logout: handleLogout,
    refreshSession: handleRefreshSession,
  };

  return (
    <OathAuthContext.Provider value={value}>
      {children}
    </OathAuthContext.Provider>
  );
};

export default OathAuthContext;
